/* eslint-disable */

const { VITE_ZX_APP_KEY, VITE_ZX_TRACK_DOMAIN, VITE_ZX_SDK_URL } = process.env;

type ShareChannelType =
  | 'ChannelWechat'
  | 'ChannelTimeLine'
  | 'ChannelSina'
  | 'ChannelQQ'
  | 'ChannelMeeTime'
  | 'ChannelAlbum';

/** 页面浏览事件数据 */
interface PageViewData {
  event_code: string;
  current_page_name: string;
  current_page_id: string;
  current_channel_name?: string;
  article_name?: string;
  article_type?: string;
  article_author?: string;
  article_id?: string;
  originality?: string;
  current_create_date?: string;
  special_topic_name?: string;
  [key: string]: unknown;
}

/** 点击事件数据 */
interface ClickData {
  current_page_name: string;
  current_page_id: string;
  button_name?: string;
  article_name?: string;
  article_type?: string;
  article_author?: string;
  article_id?: string;
  originality?: string;
  current_create_date?: string;
  special_topic_name?: string;
  share_channel_name?: string;
  [key: string]: unknown;
}

/** 其他/结果事件数据 */
interface OtherData {
  current_page_name: string;
  current_page_id: string;
  button_name?: string;
  is_successful?: string;
  article_name?: string;
  article_type?: string;
  article_author?: string;
  article_id?: string;
  originality?: string;
  current_create_date?: string;
  special_topic_name?: string;
  share_channel_name?: string;
  [key: string]: unknown;
}

/**
 * 中宣部 H5 埋点封装（基于 QuickTracking Web SDK）
 *
 * 说明：
 * - 在 App WebView 内打开的 H5 页面，由 App 端自行埋点，H5 不再重复上报
 * - 微信分享、外部浏览器等场景打开的 H5 页面，由 H5 自己走本 SDK 上报
 */
class ZhongXuan {
  /** SDK 是否已真正初始化完成（aplusReady complete） */
  static #isInit = false;
  /** SDK 是否已开始加载 */
  static #loading = false;
  /** SDK 就绪前暂存的事件，就绪后统一重放，避免时序竞争导致 sendPV 丢失 */
  static #pendingEvents: { action: string; args: unknown[] }[] = [];

  /**
   * 判断当前是否在 App WebView 内
   * 通过 UA 中的 App 标识判断，具体标识需与 App 端对齐
   */
  static #isInAppWebView() {
    const ua = navigator.userAgent;
    // 项目现有 App 标识，如 App 端有其他标识可在此补充
    return ua.indexOf('pdmiryun') > -1 || ua.indexOf('rmcb') > -1;
  }

  /**
   * 判断 H5 是否需要自己埋点
   */
  static #shouldTrackByH5() {
    return !this.#isInAppWebView();
  }

  static #loadScript(
    url: string,
    id: string,
    loadCb?: () => void,
    errorCb?: (err?: Event | string) => void,
  ) {
    const existScript = document.querySelector(`script[id="${id}"]`);
    if (existScript) {
      loadCb?.();
      return;
    }

    const firstScript = document.getElementsByTagName('script')[0];
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.id = id;
    script.src = url;
    script.onload = () => {
      loadCb?.();
    };
    script.onerror = (err) => {
      errorCb?.(err);
    };

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }

  /**
   * 初始化
   */
  static init() {
    // if (process.env.NODE_ENV === 'development') return Promise.resolve(false);
    console.log('[ZhongXuan] env check:', {
      VITE_ZX_SDK_URL,
      VITE_ZX_APP_KEY,
      VITE_ZX_TRACK_DOMAIN,
    });
    if (!this.#shouldTrackByH5()) return Promise.resolve(false);
    if (this.#isInit) return Promise.resolve(true);

    if (!VITE_ZX_SDK_URL || !VITE_ZX_APP_KEY) {
      console.warn('[ZhongXuan] SDK 地址或 AppKey 未配置');
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve, reject) => {
      // 先于脚本初始化队列，保证 meta 信息能被 SDK 消费到
      window.aplus_queue = window.aplus_queue || [];

      // 订阅 SDK 初始化完成事件
      window.aplus_queue.push({
        action: 'aplus.aplus_pubsub.subscribe',
        arguments: [
          'aplusReady',
          (status: string) => {
            if (status === 'complete') {
              this.#isInit = true;
              this.#flushPendingEvents();
              resolve(true);
            }
          },
        ],
      });

      window.aplus_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: ['appKey', VITE_ZX_APP_KEY],
      });

      if (VITE_ZX_TRACK_DOMAIN) {
        window.aplus_queue.push({
          action: 'aplus.setMetaInfo',
          arguments: ['trackDomain', VITE_ZX_TRACK_DOMAIN],
        });
      }

      // 全局关闭自动 PV：所有页面只走手动 sendPV，避免自动 PV 与手动 sendPV 重复上报（两条）。
      window.aplus_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: ['aplus-disable-apv', true],
      });

      // 补充通用属性（globalproperty），避免 SDK 消费队列时读取不到
      // 必要元信息而抛出 "Cannot read properties of undefined (reading 'O')"
      window.aplus_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: [
          'globalproperty',
          {
            isLogin: 'false',
            user_id: '',
            channel_code: 'h5',
            custom_param: {
              source: 'h5',
            },
          },
        ],
      });

      // 页面配置：将稿件详情页 / 专题页 / 直播页 URL 匹配到 article_details_pg，并 skipMe 关闭自动 PV，
      // 避免 SDK 自动 PV 与手动 sendPV 冲突/覆盖，确保手动 sendPV 的事件编码与页面编码可控。
      // 注意：regRule 正则需与实际的稿件详情页 / 专题页 / 直播页 URL 格式保持一致。
      window.aplus_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: [
          'pageConfig',
          {
            '/pages/': {
              pageName: 'article_details_pg',
              pageTitle: '稿件详情页',
              regRule: /^\/pages\/\d{4}\/\d{2}\/\d{2}\/[0-9a-f]{32}\.html$/,
              skipMe: true,
            },
            '/topic/': {
              pageName: 'article_details_pg',
              pageTitle: '专题页',
              regRule: /^\/topic\/\d{4}\/\d{2}\/\d{2}\/[0-9a-f]{32}\.html$/,
              skipMe: true,
            },
            '/live/': {
              pageName: 'article_details_pg',
              pageTitle: '直播页',
              regRule: /^\/live\/\d{4}\/\d{2}\/\d{2}\/[0-9a-f]{32}\.html$/,
              skipMe: true,
            },
          },
        ],
      });

      // 防重复加载 SDK
      if (this.#loading) return;
      this.#loading = true;

      this.#loadScript(
        VITE_ZX_SDK_URL,
        'beacon-aplus-zhongxuan',
        () => {},
        (err) => {
          console.error('[ZhongXuan] 脚本加载失败', err);
          reject(err);
        },
      );

      // 兜底：SDK 异常慢时，延后再尝试 resolve（默认 5 秒）。
      // 注意：这里不立即 resolve，而是先尝试通过 init 内部的重试逻辑判断，
      // 若仍不就绪则在 flush 时由 #push 决定暂存，避免误触发导致 sendPV 丢失。
      setTimeout(() => {
        if (!this.#isInit) {
          console.warn(
            '[ZhongXuan] SDK 5s 内未触发 aplusReady，将继续尝试，事件将暂存待就绪后重放',
          );
          // 不 resolve，等待 SDK 后续补触发 aplusReady；
          // 若 SDK 完全失败，事件会暂存在 pendingEvents 中，
          // 由上层决定后续是否强制补发。
          resolve(false);
        }
      }, 5000);
    });
  }

  /** 将 SDK 就绪前暂存的事件重放给 aplus_queue */
  static #flushPendingEvents() {
    if (!this.#pendingEvents.length) return;
    console.log('[ZhongXuan] 重放暂存事件数:', this.#pendingEvents.length);
    const pending = this.#pendingEvents.splice(0);
    for (const { action, args } of pending) {
      window.aplus_queue?.push({ action, arguments: args });
    }
  }

  /**
   * 统一推入 aplus_queue
   * SDK 未真正就绪（aplusReady complete）前，事件先暂存，就绪后重放，
   * 避免时序竞争导致 sendPV 在 SDK 初始化完成前被消费而抛错。
   */
  static #push(action: string, args: unknown[]) {
    // if (process.env.NODE_ENV === 'development') return;
    if (!this.#shouldTrackByH5()) return;

    if (!window.aplus_queue) {
      console.warn('[ZhongXuan] aplus_queue 未初始化，事件暂存:', action);
      this.#pendingEvents.push({ action, args });
      return;
    }

    // SDK 尚未初始化完成：先暂存，等 aplusReady 后统一重放
    if (!this.#isInit) {
      console.log('[ZhongXuan] SDK 未就绪，事件暂存:', action);
      this.#pendingEvents.push({ action, args });
      return;
    }

    console.log('[ZhongXuan] push:', action, JSON.parse(JSON.stringify(args))); // 临时调试
    window.aplus_queue.push({ action, arguments: args });
  }

  /**
   * 页面浏览事件
   */
  static pageView(data: PageViewData) {
    this.#push('aplus.sendPV', [
      { is_auto: false },
      { ...data, event_type: 'pageview' },
    ]);
  }

  /**
   * 点击事件
   * @param eventCode 事件编码，如 article_details_clk
   */
  static click(eventCode: string, data: ClickData) {
    this.#push('aplus.record', [
      eventCode,
      'CLK',
      { ...data, event_type: 'click', event_code: eventCode },
    ]);
  }

  /**
   * 其他/结果事件
   * @param eventCode 事件编码，如 article_publish_result
   */
  static other(eventCode: string, data: OtherData) {
    this.#push('aplus.record', [
      eventCode,
      'OTHER',
      { ...data, event_type: 'other', event_code: eventCode },
    ]);
  }

  /**
   * 分享事件（基于其他事件包装）
   */
  static shareArticle(articleId: string, shareChannel: ShareChannelType, extra: Record<string, unknown> = {}) {
    this.other('article_share_result', {
      article_id: articleId,
      share_channel_name: shareChannel,
      ...extra,
    } as OtherData);
  }
}

export default ZhongXuan;
